import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { prefersReducedMotion } from '../lib/prefersReducedMotion';
import { WebGPUShaderEngine } from '../lib/webgpuEngine';

// Hex to RGB parser helper
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : [10, 12, 20];
}

// Lerp helper functions
const lerp = (start, end, amt) => (1 - amt) * start + amt * end;
const lerpColor = (c1, c2, amt) => [
  lerp(c1[0], c2[0], amt),
  lerp(c1[1], c2[1], amt),
  lerp(c1[2], c2[2], amt)
];

// Dynamic Theme Parser
function getThemeFromEnvironment(envText) {
  const env = (envText || '').toLowerCase();
  
  // Default Space Theme (Deep indigo / starlight night)
  const theme = {
    bgColors: [
      hexToRgb('#06080f'),
      hexToRgb('#0d0f1a'),
      hexToRgb('#0a0c1d'),
      hexToRgb('#080a16')
    ],
    nebulae: [
      { xPct: 0.75, yPct: 0.25, rPct: 0.28, c: [80, 60, 180], opacity: 0.06 }, // Indigo
      { xPct: 0.2,  yPct: 0.7,  rPct: 0.22, c: [40, 80, 160], opacity: 0.06 }, // Sapphire
      { xPct: 0.5,  yPct: 0.5,  rPct: 0.18, c: [60, 30, 120], opacity: 0.06 }  // Purple
    ],
    starCount: 340,
    speedMultiplier: 1.0,
    starColours: [
      [255, 255, 255],
      [200, 215, 255],
      [180, 195, 255],
      [255, 240, 210]
    ]
  };

  if (env.includes('violet') || env.includes('nebula') || env.includes('shadow') || env.includes('purple')) {
    // Magenta-violet deep space
    theme.bgColors = [
      hexToRgb('#08040d'),
      hexToRgb('#140822'),
      hexToRgb('#1a052e'),
      hexToRgb('#0d0416')
    ];
    theme.nebulae = [
      { xPct: 0.7,  yPct: 0.2,  rPct: 0.35, c: [180, 50, 230], opacity: 0.08 }, // Fuchsia
      { xPct: 0.3,  yPct: 0.75, rPct: 0.28, c: [100, 20, 150], opacity: 0.08 }, // Violet
      { xPct: 0.55, yPct: 0.45, rPct: 0.25, c: [139, 92, 246], opacity: 0.08 }  // Purple
    ];
    theme.starColours = [
      [255, 240, 255],
      [232, 190, 255],
      [255, 255, 255],
      [216, 180, 254]
    ];
    theme.speedMultiplier = 0.6; // Slow, floating space drift
  } 
  else if (env.includes('gold') || env.includes('amber') || env.includes('star') || env.includes('dying star')) {
    // Amber / Golden starlight corridor
    theme.bgColors = [
      hexToRgb('#080502'),
      hexToRgb('#180e04'),
      hexToRgb('#241407'),
      hexToRgb('#0f0903')
    ];
    theme.nebulae = [
      { xPct: 0.8,  yPct: 0.3,  rPct: 0.32, c: [234, 140, 8], opacity: 0.08 },  // Amber
      { xPct: 0.25, yPct: 0.65, rPct: 0.25, c: [180, 83, 9], opacity: 0.08 },   // Warm rust orange
      { xPct: 0.5,  yPct: 0.4,  rPct: 0.20, c: [250, 204, 21], opacity: 0.08 }  // Bright gold
    ];
    theme.starColours = [
      [255, 255, 255],
      [254, 240, 138], // Yellow
      [253, 186, 116], // Orange-yellow
      [255, 253, 230]
    ];
    theme.speedMultiplier = 1.3; // Rhythmic fusion flow
  } 
  else if (env.includes('crystal') || env.includes('ice') || env.includes('sapphire') || env.includes('crystalline') || env.includes('rings')) {
    // Cold sapphire starlight corridors
    theme.bgColors = [
      hexToRgb('#020612'),
      hexToRgb('#05112e'),
      hexToRgb('#0b1f4d'),
      hexToRgb('#030b1e')
    ];
    theme.nebulae = [
      { xPct: 0.75, yPct: 0.25, rPct: 0.3,  c: [14, 165, 233], opacity: 0.08 }, // Cyan
      { xPct: 0.2,  yPct: 0.7,  rPct: 0.25, c: [29, 78, 216], opacity: 0.08 },  // Sapphire blue
      { xPct: 0.5,  yPct: 0.5,  rPct: 0.2,  c: [56, 189, 248], opacity: 0.08 }  // Ice blue
    ];
    theme.starColours = [
      [255, 255, 255],
      [186, 230, 253], // Sky blue
      [224, 242, 254],
      [165, 180, 252]
    ];
    theme.speedMultiplier = 1.8; // Swift icy glide
  }
  else if (env.includes('void') || env.includes('starless') || env.includes('quiet expanse') || env.includes('cradle')) {
    // Starless grand void
    theme.bgColors = [
      hexToRgb('#010103'),
      hexToRgb('#030306'),
      hexToRgb('#06060c'),
      hexToRgb('#020204')
    ];
    theme.nebulae = [
      { xPct: 0.5, yPct: 0.5, rPct: 0.4, c: [30, 30, 45], opacity: 0.02 }, // Extremely dim slate-grey dust
      { xPct: 0.2, yPct: 0.2, rPct: 0.2, c: [10, 10, 15], opacity: 0.01 },
      { xPct: 0.8, yPct: 0.8, rPct: 0.3, c: [10, 10, 15], opacity: 0.01 }
    ];
    theme.starCount = 45; // Deplete stars for an empty, vast isolation feel
    theme.starColours = [
      [200, 200, 200],
      [150, 160, 180]
    ];
    theme.speedMultiplier = 0.25; // Near stationary drifting
  }
  else if (env.includes('shower') || env.includes('meteors') || env.includes('comet') || env.includes('dust')) {
    // Comet shower (Silver & stardust silver)
    theme.bgColors = [
      hexToRgb('#050811'),
      hexToRgb('#0b1326'),
      hexToRgb('#14203d'),
      hexToRgb('#070c18')
    ];
    theme.nebulae = [
      { xPct: 0.8,  yPct: 0.2,  rPct: 0.3,  c: [226, 232, 240], opacity: 0.07 }, // Silver cloud
      { xPct: 0.3,  yPct: 0.7,  rPct: 0.26, c: [71, 85, 105], opacity: 0.07 },   // Dark slate cloud
      { xPct: 0.55, yPct: 0.45, rPct: 0.22, c: [148, 163, 184], opacity: 0.07 }  // Steel blue
    ];
    theme.starColours = [
      [255, 255, 255],
      [241, 245, 249],
      [203, 213, 225]
    ];
    theme.speedMultiplier = 2.8; // Speedy flight passing shower
  }

  return theme;
}

const StarfieldBackground = () => {
  const canvasRef = useRef(null);
  const [bodyElement, setBodyElement] = useState(null);
  const [currentEnv, setCurrentEnv] = useState(() => {
    return localStorage.getItem('sacredmothr_current_environment') || "coasting in the shadow of a violet gas giant";
  });
  const [astronomicalContext, setAstronomicalContext] = useState(() => {
    try {
      const saved = localStorage.getItem('sacredmothr_active_astronomical_context');
      return saved ? JSON.parse(saved) : null;
    } catch(e) { return null; }
  });

  // Track state ref to access latest in the rendering loop without closures locking
  const envThemeRef = useRef(getThemeFromEnvironment(currentEnv));

  useEffect(() => {
    envThemeRef.current = getThemeFromEnvironment(currentEnv);
  }, [currentEnv]);

  // Event listener for changes
  useEffect(() => {
    const handleEnvUpdate = (e) => {
      if (e.detail?.env) {
        setCurrentEnv(e.detail.env);
      }
    };
    const handleAstroUpdate = (e) => {
      if (e.detail?.context) {
        setAstronomicalContext(e.detail.context);
      }
    };
    window.addEventListener('sacredmothr_env_update', handleEnvUpdate);
    window.addEventListener('sacredmothr_astronomical_update', handleAstroUpdate);
    return () => {
      window.removeEventListener('sacredmothr_env_update', handleEnvUpdate);
      window.removeEventListener('sacredmothr_astronomical_update', handleAstroUpdate);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    const isMobileDevice = window.innerWidth <= 768;
    const reduceMotion = prefersReducedMotion();

    const handleResize = () => {
      if (canvas) {
        // On mobile cap the overscroll expansion to 1.1x (not 1.5x) to save GPU memory
        const overscroll = isMobileDevice ? 1.1 : 1.5;
        canvas.width = window.innerWidth * overscroll;
        canvas.height = window.innerHeight * overscroll;
      }
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', () => {
      setTimeout(handleResize, 100);
      setTimeout(handleResize, 400); // iOS Safari often needs a delayed second pass after rotation
    });
    handleResize();

    // Reduce star count on mobile to ease GPU load (340 -> 160)
    const numStars = isMobileDevice ? 160 : 340;
    const stars = [];
    const initialTheme = envThemeRef.current;
    
    for (let i = 0; i < numStars; i++) {
      const depth = Math.random();
      const defaultColour = initialTheme.starColours[Math.floor(Math.random() * initialTheme.starColours.length)];
      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: depth * 1.8 + 0.3,
        defaultSpeed: depth * 0.22 + 0.03,
        speed: depth * 0.22 + 0.03,
        defaultBaseOpacity: depth * 0.55 + 0.2,
        currentBaseOpacity: depth * 0.55 + 0.2, // will be lerped
        opacity: depth * 0.55 + 0.2,
        twinkleSpeed: Math.random() * 0.018 + 0.004,
        twinkleOffset: Math.random() * Math.PI * 2,
        colour: [...defaultColour], // mutable current color
        targetColour: [...defaultColour] // color we lerp towards
      });
    }

    // Volumetric celestial bodies spawner queue
    const celestialBodies = [];
    try {
      const savedBody = localStorage.getItem('sacredmothr_active_celestial_body');
      if (savedBody) {
        const parsed = JSON.parse(savedBody);
        if (parsed && parsed.y > -200 && parsed.size > 0 && isFinite(parsed.size)) {
          celestialBodies.push(parsed);
        }
      }
    } catch (e) {
      console.warn("Failed to load active celestial body from localStorage", e);
    }
    let lastSpawnTime = Date.now() - 35000; // Trigger first spawn in 5 seconds!

    const spawnCelestialBody = (query) => {
      const targetQuery = (query || '').toLowerCase().trim();
      let type = 'gas_giant';
      let name = 'Cosmic Oasis';
      
      if (targetQuery.includes('kepler')) {
        type = 'exoplanet';
        name = 'Kepler-186f';
      } else if (targetQuery.includes('centauri') || targetQuery.includes('sirius') || targetQuery.includes('eridani')) {
        type = 'star';
        name = query.replace(/_/g, ' ');
      } else if (targetQuery.includes('nebula') || targetQuery.includes('pillars')) {
        type = 'nebula_core';
        name = query.replace(/_/g, ' ');
      } else if (targetQuery.includes('galaxy') || targetQuery.includes('cluster') || targetQuery.includes('sombrero')) {
        type = 'galaxy';
        name = query.replace(/_/g, ' ');
      } else if (targetQuery.includes('oort') || targetQuery.includes('comet') || targetQuery.includes('drift')) {
        type = 'comet';
        name = 'Oort Cloud Comet';
      }

      const size = Math.random() * 30 + 55; // 55px to 85px radius
      const body = {
        id: Math.random().toString(36).substring(2),
        x: Math.random() * (canvas.width - 300) + 150,
        y: canvas.height + 150,
        size,
        speed: Math.random() * 0.0001 + 0.0002, // Extremely slow speed so it stays on screen for ~16-24 hours!
        type,
        name,
        opacity: 0,
        targetOpacity: 0.85,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: Math.random() * 0.0001 - 0.00005,
        pulse: Math.random() * Math.PI * 2,
        pulseSpeed: Math.random() * 0.001 + 0.0005
      };
      celestialBodies.push(body);
      try {
        localStorage.setItem('sacredmothr_active_celestial_body', JSON.stringify(body));
      } catch (e) {}
    };

    // Active drawing parameters initialized to start theme
    let activeSpeedMultiplier = initialTheme.speedMultiplier;
    let activeBgColors = initialTheme.bgColors.map(c => [...c]);
    let activeNebulae = initialTheme.nebulae.map(n => ({
      xPct: n.xPct,
      yPct: n.yPct,
      rPct: n.rPct,
      c: [...n.c],
      opacity: n.opacity
    }));

    let tick = 0;
    const lerpRate = 0.015; // smooth 2-3s transition at 60fps

    let lastTime = 0;
    const fps = 30;
    const interval = 1000 / fps;

    const render = (time) => {
      if (document.visibilityState === 'hidden') {
        animationFrameId = requestAnimationFrame(render);
        return;
      }
      if (!reduceMotion) {
        animationFrameId = requestAnimationFrame(render);
      }
      if (!time) time = performance.now();
      const deltaTime = time - lastTime;
      if (deltaTime < interval) return;
      lastTime = time - (deltaTime % interval);

      // Skip frame if canvas has no area (e.g. before iOS viewport is ready)
      if (!canvas.width || !canvas.height) return;

      tick++;
      const targetTheme = envThemeRef.current;

      // Spawner check (every 40 seconds)
      const nowTime = Date.now();
      if (nowTime - lastSpawnTime > 40000) {
        if (celestialBodies.length === 0) {
          try {
            const saved = localStorage.getItem('sacredmothr_active_astronomical_context');
            const context = saved ? JSON.parse(saved) : null;
            
            const lastSpawnedMilestone = localStorage.getItem('sacredmothr_last_spawned_milestone');
            const currentMilestone = context?.search_query || 'Oort_cloud';
            
            if (lastSpawnedMilestone !== currentMilestone) {
              spawnCelestialBody(currentMilestone);
              localStorage.setItem('sacredmothr_last_spawned_milestone', currentMilestone);
            } else if (Math.random() < 0.15) {
              // 15% chance to spawn a generic rogue body (gas giant/comet) when in deep void
              spawnCelestialBody('Oort_cloud');
            }
          } catch (e) {
            spawnCelestialBody('Oort_cloud');
          }
        }
        lastSpawnTime = nowTime;
      }

      // 1. Lerp dynamic values at 60fps
      activeSpeedMultiplier = lerp(activeSpeedMultiplier, targetTheme.speedMultiplier, lerpRate);

      // Lerp Background colors
      for (let i = 0; i < 4; i++) {
        activeBgColors[i] = lerpColor(activeBgColors[i], targetTheme.bgColors[i], lerpRate);
      }

      // Lerp Nebula coordinates and colors
      for (let i = 0; i < 3; i++) {
        const active = activeNebulae[i];
        const target = targetTheme.nebulae[i];
        
        active.xPct = lerp(active.xPct, target.xPct, lerpRate);
        active.yPct = lerp(active.yPct, target.yPct, lerpRate);
        active.rPct = lerp(active.rPct, target.rPct, lerpRate);
        active.opacity = lerp(active.opacity, target.opacity, lerpRate);
        active.c = lerpColor(active.c, target.c, lerpRate);
      }

      // 2. Render deep space background with lerped colors
      const bg = ctx.createLinearGradient(0, 0, canvas.width * 0.6, canvas.height);
      const c0 = activeBgColors[0];
      const c1 = activeBgColors[1];
      const c2 = activeBgColors[2];
      const c3 = activeBgColors[3];
      bg.addColorStop(0,   `rgb(${Math.round(c0[0])}, ${Math.round(c0[1])}, ${Math.round(c0[2])})`);
      bg.addColorStop(0.4, `rgb(${Math.round(c1[0])}, ${Math.round(c1[1])}, ${Math.round(c1[2])})`);
      bg.addColorStop(0.7, `rgb(${Math.round(c2[0])}, ${Math.round(c2[1])}, ${Math.round(c2[2])})`);
      bg.addColorStop(1,   `rgb(${Math.round(c3[0])}, ${Math.round(c3[1])}, ${Math.round(c3[2])})`);
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // 3. Draw lerped subtle nebula blobs
      activeNebulae.forEach(({ xPct, yPct, rPct, c, opacity }) => {
        const x = canvas.width * xPct;
        const y = canvas.height * yPct;
        const r = canvas.width * rPct;
        
        const g = ctx.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0,   `rgba(${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])},${opacity})`);
        g.addColorStop(1,   `rgba(${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])},0)`);
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      });

      // 4. Render and update stars
      stars.forEach((star, index) => {
        // Adjust star speed based on the environment speed multiplier
        star.speed = star.defaultSpeed * activeSpeedMultiplier;

        // Star depletion mechanism: fade out stars beyond the current target starCount
        const targetOpacityBase = (index < targetTheme.starCount) ? star.defaultBaseOpacity : 0;
        star.currentBaseOpacity = lerp(star.currentBaseOpacity, targetOpacityBase, lerpRate);

        // Periodically verify if star needs a new color target when theme changes
        // (Smoothly cross-fades individual star colors)
        if (tick % 120 === 0 || star.targetColour.toString() === star.colour.toString()) {
          const nextColor = targetTheme.starColours[Math.floor(Math.random() * targetTheme.starColours.length)];
          star.targetColour = [...nextColor];
        }
        star.colour = lerpColor(star.colour, star.targetColour, lerpRate * 0.5); // slightly slower color shift

        // Twinkle calculation
        const twinkle = Math.sin(tick * star.twinkleSpeed + star.twinkleOffset);
        star.opacity = Math.max(0, Math.min(1, star.currentBaseOpacity + twinkle * 0.15));

        const r = Math.round(star.colour[0]);
        const g = Math.round(star.colour[1]);
        const b = Math.round(star.colour[2]);

        // Draw radial glow for larger bright stars
        if (star.size > 1.3 && star.opacity > 0.05) {
          const glow = ctx.createRadialGradient(star.x, star.y, 0, star.x, star.y, star.size * 4);
          glow.addColorStop(0, `rgba(${r},${g},${b},${star.opacity * 0.45})`);
          glow.addColorStop(1, `rgba(${r},${g},${b},0)`);
          ctx.fillStyle = glow;
          ctx.beginPath();
          ctx.arc(star.x, star.y, star.size * 4, 0, Math.PI * 2);
          ctx.fill();
        }

        if (star.opacity > 0.01) {
          ctx.fillStyle = `rgba(${r},${g},${b},${star.opacity})`;
          ctx.beginPath();
          ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
          ctx.fill();
        }

        // Float stars upward
        star.y -= star.speed;
        if (star.y < -10) {
          star.y = canvas.height + 10;
          star.x = Math.random() * canvas.width;
        }
      });

      // 5. Render celestial bodies in parallax
      celestialBodies.forEach((body) => {
        body.y -= body.speed;
        body.rotation += body.rotationSpeed;
        body.pulse += body.pulseSpeed;

        // Fade in when entering, fade out when leaving
        if (body.y > canvas.height) {
          body.opacity = lerp(body.opacity, body.targetOpacity, 0.02);
        } else if (body.y < 150) {
          body.opacity = lerp(body.opacity, 0, 0.04);
        } else {
          body.opacity = lerp(body.opacity, body.targetOpacity, 0.02);
        }

        ctx.save();
        ctx.translate(body.x, body.y);
        ctx.rotate(body.rotation);
        ctx.globalAlpha = body.opacity;

        const size = (body.size > 0 && isFinite(body.size)) ? body.size : 55;
        const color = targetTheme.starColours[0] || [255, 255, 255];
        const r = color[0];
        const g = color[1];
        const b = color[2];

        if (body.type === 'exoplanet') {
          // A beautiful habitable planet (Kepler-186f style)
          // Atmosphere corona glow
          const atm = ctx.createRadialGradient(0, 0, size * 0.8, 0, 0, size * 1.3);
          atm.addColorStop(0, `rgba(56, 189, 248, 0.4)`);
          atm.addColorStop(0.5, `rgba(56, 189, 248, 0.15)`);
          atm.addColorStop(1, `rgba(56, 189, 248, 0)`);
          ctx.fillStyle = atm;
          ctx.beginPath();
          ctx.arc(0, 0, size * 1.3, 0, Math.PI * 2);
          ctx.fill();

          // Planet surface sphere
          const sphere = ctx.createLinearGradient(-size, -size, size, size);
          sphere.addColorStop(0, '#10b981'); // Emerald green continent
          sphere.addColorStop(0.5, '#0284c7'); // Sapphire blue ocean
          sphere.addColorStop(1, '#0369a1'); // Deep navy
          ctx.fillStyle = sphere;
          ctx.beginPath();
          ctx.arc(0, 0, size, 0, Math.PI * 2);
          ctx.fill();

          // Swirling white clouds
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
          ctx.lineWidth = size * 0.08;
          for (let cy = -size * 0.7; cy < size * 0.7; cy += size * 0.3) {
            const rx = Math.sqrt(size * size - cy * cy);
            ctx.beginPath();
            ctx.ellipse(0, cy, rx * 0.9, size * 0.08, 0, 0, Math.PI);
            ctx.stroke();
          }

          // 3D Shadow Overlay (creates dynamic lighting)
          const shadow = ctx.createRadialGradient(size * 0.3, -size * 0.3, size * 0.4, size * 0.2, -size * 0.2, size * 1.1);
          shadow.addColorStop(0, 'rgba(0, 0, 0, 0)');
          shadow.addColorStop(0.8, 'rgba(3, 7, 18, 0.85)');
          shadow.addColorStop(1, 'rgba(3, 7, 18, 0.98)');
          ctx.fillStyle = shadow;
          ctx.beginPath();
          ctx.arc(0, 0, size * 1.02, 0, Math.PI * 2);
          ctx.fill();

        } else if (body.type === 'star') {
          // A pulsing giant star (Proxima Centauri style)
          const scale = 1.0 + Math.sin(body.pulse) * 0.04;
          
          // Corona outer halo
          const corona = ctx.createRadialGradient(0, 0, size * 0.5 * scale, 0, 0, size * 1.6 * scale);
          corona.addColorStop(0, `rgba(239, 68, 68, 0.5)`); // Deep Red
          corona.addColorStop(0.5, `rgba(249, 115, 22, 0.2)`); // Orange
          corona.addColorStop(1, `rgba(253, 186, 116, 0)`);
          ctx.fillStyle = corona;
          ctx.beginPath();
          ctx.arc(0, 0, size * 1.6 * scale, 0, Math.PI * 2);
          ctx.fill();

          // Star burning core
          const core = ctx.createRadialGradient(0, 0, 0, 0, 0, size * scale);
          core.addColorStop(0, '#ffffff');
          core.addColorStop(0.2, '#fef08a'); // yellow
          core.addColorStop(0.6, '#f97316'); // orange
          core.addColorStop(1, '#dc2626'); // red
          ctx.fillStyle = core;
          ctx.beginPath();
          ctx.arc(0, 0, size * scale, 0, Math.PI * 2);
          ctx.fill();

          // Animated solar rays
          ctx.strokeStyle = 'rgba(251, 146, 60, 0.25)';
          ctx.lineWidth = 1.5;
          for (let i = 0; i < 8; i++) {
            const angle = (i * Math.PI) / 4 + body.pulse * 0.2;
            const len = size * (1.2 + Math.sin(body.pulse + i) * 0.15) * scale;
            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.lineTo(Math.cos(angle) * len, Math.sin(angle) * len);
            ctx.stroke();
          }

        } else if (body.type === 'nebula_core') {
          // Overlapping volumetric gas blobs (Orion/Crab Nebula style)
          for (let i = 0; i < 5; i++) {
            const offsetAngle = (i * Math.PI * 2) / 5 + body.pulse * 0.08;
            const dist = size * 0.4;
            const bx = Math.cos(offsetAngle) * dist;
            const by = Math.sin(offsetAngle) * dist;
            const br = size * (0.8 + Math.sin(body.pulse * 0.5 + i) * 0.15);
            
            const g = ctx.createRadialGradient(bx, by, 0, bx, by, br);
            const c = (i % 3 === 0) ? [168, 85, 247] : (i % 3 === 1) ? [236, 72, 153] : [59, 130, 246];
            g.addColorStop(0, `rgba(${c[0]}, ${c[1]}, ${c[2]}, 0.12)`);
            g.addColorStop(0.6, `rgba(${c[0]}, ${c[1]}, ${c[2]}, 0.04)`);
            g.addColorStop(1, 'rgba(0, 0, 0, 0)');
            
            ctx.fillStyle = g;
            ctx.beginPath();
            ctx.arc(bx, by, br, 0, Math.PI * 2);
            ctx.fill();
          }

        } else if (body.type === 'galaxy') {
          // A rotating spiral galaxy (Andromeda style)
          // Core glow
          const core = ctx.createRadialGradient(0, 0, 0, 0, 0, size * 0.6);
          core.addColorStop(0, '#ffffff');
          core.addColorStop(0.3, 'rgba(168, 85, 247, 0.45)');
          core.addColorStop(1, 'rgba(168, 85, 247, 0)');
          ctx.fillStyle = core;
          ctx.beginPath();
          ctx.arc(0, 0, size * 0.6, 0, Math.PI * 2);
          ctx.fill();

          // Symmetrical spiral arms
          ctx.fillStyle = 'rgba(216, 180, 254, 0.65)';
          const numParticles = 60;
          for (let i = 0; i < numParticles; i++) {
            const theta = (i / numParticles) * Math.PI * 4;
            const rOffset = size * 0.2 + (i / numParticles) * size * 1.1;
            
            // Arm 1
            const x1 = Math.cos(theta) * rOffset;
            const y1 = Math.sin(theta) * rOffset;
            const sizePart1 = (1 - (i / numParticles)) * 2.2 + 0.4;
            ctx.beginPath();
            ctx.arc(x1, y1, sizePart1, 0, Math.PI * 2);
            ctx.fill();

            // Arm 2
            const x2 = Math.cos(theta + Math.PI) * rOffset;
            const y2 = Math.sin(theta + Math.PI) * rOffset;
            ctx.beginPath();
            ctx.arc(x2, y2, sizePart1, 0, Math.PI * 2);
            ctx.fill();
          }

        } else if (body.type === 'comet') {
          // A beautiful Oort Cloud comet
          // Vapor tail
          const tail = ctx.createLinearGradient(0, 0, -size * 1.5, size * 1.5);
          tail.addColorStop(0, 'rgba(56, 189, 248, 0.38)');
          tail.addColorStop(0.4, 'rgba(99, 102, 241, 0.18)');
          tail.addColorStop(1, 'rgba(99, 102, 241, 0)');
          ctx.fillStyle = tail;
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.lineTo(-size * 1.5 - size * 0.25, size * 1.5 - size * 0.25);
          ctx.lineTo(-size * 1.5 + size * 0.25, size * 1.5 + size * 0.25);
          ctx.closePath();
          ctx.fill();

          // Nucleus glow
          const nucleus = ctx.createRadialGradient(0, 0, 0, 0, 0, size * 0.22);
          nucleus.addColorStop(0, '#ffffff');
          nucleus.addColorStop(0.5, '#e0f2fe');
          nucleus.addColorStop(1, 'rgba(14, 165, 233, 0)');
          ctx.fillStyle = nucleus;
          ctx.beginPath();
          ctx.arc(0, 0, size * 0.22, 0, Math.PI * 2);
          ctx.fill();
        } else {
          // Fallback Gas Giant (with concentric rings)
          // Atmosphere scattering
          const atm = ctx.createRadialGradient(0, 0, size * 0.8, 0, 0, size * 1.25);
          atm.addColorStop(0, `rgba(168, 85, 247, 0.35)`);
          atm.addColorStop(0.6, `rgba(168, 85, 247, 0.1)`);
          atm.addColorStop(1, `rgba(168, 85, 247, 0)`);
          ctx.fillStyle = atm;
          ctx.beginPath();
          ctx.arc(0, 0, size * 1.25, 0, Math.PI * 2);
          ctx.fill();

          // Behind Rings
          ctx.strokeStyle = 'rgba(216, 180, 254, 0.3)';
          ctx.lineWidth = size * 0.16;
          ctx.beginPath();
          ctx.ellipse(0, 0, size * 1.6, size * 0.28, Math.PI / 8, Math.PI, Math.PI * 2);
          ctx.stroke();

          // Planet body sphere
          const sphere = ctx.createLinearGradient(-size, -size, size, size);
          sphere.addColorStop(0, `rgb(${r}, ${g}, ${b})`);
          sphere.addColorStop(1, '#090714');
          ctx.fillStyle = sphere;
          ctx.beginPath();
          ctx.arc(0, 0, size, 0, Math.PI * 2);
          ctx.fill();

          // Front Rings
          ctx.strokeStyle = 'rgba(216, 180, 254, 0.6)';
          ctx.beginPath();
          ctx.ellipse(0, 0, size * 1.6, size * 0.28, Math.PI / 8, 0, Math.PI);
          ctx.stroke();
        }

        ctx.restore();
      });

      // Periodically save the body's updated coordinate to localStorage (every 5 seconds / 300 frames)
      if (tick % 300 === 0 && celestialBodies.length > 0) {
        try {
          localStorage.setItem('sacredmothr_active_celestial_body', JSON.stringify(celestialBodies[0]));
        } catch (e) {}
      }

      // Filter out completed celestial bodies that went off screen
      for (let i = celestialBodies.length - 1; i >= 0; i--) {
        if (celestialBodies[i].y < -200) {
          celestialBodies.splice(i, 1);
          try {
            localStorage.removeItem('sacredmothr_active_celestial_body');
          } catch (e) {}
        }
      }
    };

    // Pause rendering when tab/app is minimized/hidden
    function handleVisibilityChange() {
      if (document.hidden) {
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
          animationFrameId = null;
        }
      } else {
        if (!animationFrameId) {
          lastTime = performance.now(); // reset time to prevent frame skip
          render();
        }
      }
    }
    if (reduceMotion) {
      // Paint one still frame instead of a continuous drifting/twinkling loop.
      render(performance.now());
    } else {
      document.addEventListener('visibilitychange', handleVisibilityChange);
      animationFrameId = requestAnimationFrame(render);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined' && document.body) {
      setBodyElement(document.body);
    }
  }, []);

  if (!bodyElement || bodyElement.nodeType !== 1 || !document.body.contains(bodyElement)) {
    return null;
  }

  try {
    return createPortal(
      <canvas
        ref={canvasRef}
        style={{
          position: 'fixed',
          top: '-25vh',
          left: '-25vw',
          width: '150vw',
          height: '150vh',
          zIndex: -1,
          pointerEvents: 'none',
        }}
      />,
      bodyElement
    );
  } catch (err) {
    console.warn('[SacredMothr] StarfieldBackground portal failed. Falling back.', err);
    return null;
  }
};

export default StarfieldBackground;
